import { createActor } from "xstate";

import { appBus } from "@src/features/events";
import { patientStore } from "@src/features/patients";
import { events } from "@src/shared/utils/observability/events";
import { PositiveInt } from "@src/shared/utils/types";

import {
  encounterMachine as baseEncounterMachine,
  type EncounterEvent,
} from "./encounter.machine";

export const encounterActor = createActor(baseEncounterMachine).start();

let previousSnapshot = encounterActor.getSnapshot();

encounterActor.subscribe((snapshot) => {
  const previous = previousSnapshot;
  previousSnapshot = snapshot;

  if (previous.value === snapshot.value) return;

  const encounterId = snapshot.context.encounterId;
  const patientId = patientStore.getState().patientId;
  if (encounterId == null || patientId == null) return;

  if (snapshot.matches("connected")) {
    events.startEncounter.track({
      encounter_id: encounterId,
      patient_id: patientId,
    });
  }

  if (snapshot.matches("in_progress")) {
    events.encounterInProgress.track({
      encounter_id: encounterId,
      patient_id: patientId,
    });
  }

  if (snapshot.matches("finalizing")) {
    events.finalizeEncounter.track({
      encounter_id: encounterId,
      patient_id: patientId,
    });
  }

  const enteredCompletionViaSse = snapshot.matches("completed_awaiting_note");
  const enteredCompletionViaNoteFirst =
    snapshot.matches("completed") &&
    (previous.matches("finish_requested") || previous.matches("finalizing"));

  if (enteredCompletionViaSse || enteredCompletionViaNoteFirst) {
    const duration_ms =
      previous.context.startedAtMs != null
        ? Math.max(0, Date.now() - previous.context.startedAtMs)
        : undefined;

    events.completeEncounter.track({
      encounter_id: encounterId,
      patient_id: patientId,
      ...(duration_ms !== undefined ? { duration_ms } : {}),
      success: true,
    });
  }
});

export function sendEncounterEvent(event: EncounterEvent): void {
  encounterActor.send(event);
}

export function matchesActiveEncounter(eventEncounterId: number): boolean {
  const active = encounterActor.getSnapshot().context.encounterId;
  return active != null && active === eventEncounterId;
}

export function registerEncounterId(id: number | undefined): void {
  const encounterId = PositiveInt.parse(id);
  if (encounterId == null) return;
  if (encounterActor.getSnapshot().context.encounterId === encounterId) {
    return;
  }

  sendEncounterEvent({
    type: "ENCOUNTER_ID_CHANGED",
    encounterId,
  });
}

/**
 * Registers client finish intent before scribe stop / navigation.
 * Registers or rekeys the encounter actor when `encounterId` is not already
 * active so the intent stays encounter-keyed across route changes.
 * Captures `completedAtBaseline` so a reopened encounter's preserved note
 * cannot satisfy note-ready until a newer completion version arrives.
 */
export function requestEncounterFinish(
  encounterId: number,
  completedAtBaseline: string | null,
): void {
  const id = PositiveInt.parse(encounterId);
  if (id == null) return;

  if (encounterActor.getSnapshot().context.encounterId !== id) {
    sendEncounterEvent({
      type: "ENCOUNTER_ID_CHANGED",
      encounterId: id,
    });
  }

  sendEncounterEvent({
    type: "FINISH_REQUESTED",
    completedAtBaseline,
  });
}

/**
 * Clears client finish intent (e.g. scribe error before navigation).
 * No-op when `encounterId` is not the active actor encounter.
 */
export function clearEncounterFinishRequest(encounterId: number): void {
  const id = PositiveInt.parse(encounterId);
  if (id == null) return;
  if (!matchesActiveEncounter(id)) return;

  sendEncounterEvent({ type: "CLEAR_FINISH_REQUESTED", encounterId: id });
}

/**
 * Marks that React Query (or another observer) has seen a generated note for
 * the active encounter. Dispatches a single keyed `NOTE_READY`; the machine
 * owns transition policy. No-op when `encounterId` is not the active actor
 * encounter.
 */
export function markEncounterNoteReady(encounterId: number): void {
  const id = PositiveInt.parse(encounterId);
  if (id == null) return;
  if (!matchesActiveEncounter(id)) return;

  sendEncounterEvent({ type: "NOTE_READY", encounterId: id });
}

/**
 * Marks that patient chrome has navigated to the visit route for note output.
 * Prevents Visit Back → patient remount from re-entering the generating UI and
 * auto-navigating to the visit again.
 */
export function markEncounterFinishHandedOff(encounterId: number): void {
  const id = PositiveInt.parse(encounterId);
  if (id == null) return;
  if (!matchesActiveEncounter(id)) return;

  sendEncounterEvent({ type: "FINISH_HANDED_OFF", encounterId: id });
}

function trackReopen(newEncounterId?: number): void {
  const encounterId = encounterActor.getSnapshot().context.encounterId;
  const patientId = patientStore.getState().patientId;
  if (encounterId == null || patientId == null) return;

  events.reopenEncounter.track({
    encounter_id: encounterId,
    patient_id: patientId,
    ...(newEncounterId != null ? { new_encounter_id: newEncounterId } : {}),
  });
}

const onTranscriptionCreated = (payload: { encounterId: number }) => {
  const encounterId = PositiveInt.parse(payload.encounterId);
  if (encounterId == null) return;
  encounterActor.send({ type: "TRANSCRIPTION_CREATED", encounterId });
};

const onEncounterSnapshot = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "SNAPSHOT_RECEIVED" });
};

const onEncounterCompleted = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "COMPLETED" });
};

const onEncounterFinalizing = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "FINALIZING" });
};

const onEncounterReopened = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  trackReopen();
  encounterActor.send({ type: "REOPENED" });
};

const onTranscriptSnapshot = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "TRANSCRIPT_SNAPSHOT_RECEIVED" });
};

const onTranscriptItemCreated = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "TRANSCRIPT_ITEM_CREATED" });
};

const onEncounterSupersededByReopen = (payload: {
  encounterId: number;
  newEncounterId: number;
}) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  const newEncounterId = PositiveInt.parse(payload.newEncounterId);
  if (newEncounterId == null) return;
  trackReopen(newEncounterId);
  encounterActor.send({ type: "SUPERSEDED", newEncounterId });
};

/**
 * Syncs patient/encounter route state. `/visit/:encounterId` omits `patientId`;
 * keep the stored patient only on same-encounter transitions (avoids racing SSE
 * handlers keyed off the active encounter). A different visit id does not prove
 * ownership of the stored patient, so clear it before registering the new encounter.
 */
const onParamsChanged = (payload: {
  params: Record<string, string | undefined>;
}) => {
  const patientId = PositiveInt.parse(payload.params.patientId);
  const encounterId = PositiveInt.parse(payload.params.encounterId);

  const currentPatientId = patientStore.getState().patientId;
  const currentEncounterId = encounterActor.getSnapshot().context.encounterId;

  if (patientId !== currentPatientId) {
    if (patientId != null) {
      patientStore.getState().setPatientId(patientId);
      encounterActor.send({ type: "PATIENT_CLEARED" });
    } else if (encounterId == null || encounterId !== currentEncounterId) {
      patientStore.getState().setPatientId(null);
      encounterActor.send({ type: "PATIENT_CLEARED" });
    }
  }

  if (encounterId !== currentEncounterId) {
    encounterActor.send({
      type: "ENCOUNTER_ID_CHANGED",
      encounterId: encounterId,
    });
  }
};

const onSearchParamsChanged = (payload: {
  searchParams: URLSearchParams;
  params: Record<string, string | undefined>;
}) => {
  const pathEncounterId = PositiveInt.parse(payload.params.encounterId);
  const searchEncounterId = PositiveInt.parse(
    payload.searchParams.get("encounterId"),
  );
  const encounterId = pathEncounterId ?? searchEncounterId;
  const currentEncounterId = encounterActor.getSnapshot().context.encounterId;

  if (encounterId !== currentEncounterId) {
    encounterActor.send({
      type: "ENCOUNTER_ID_CHANGED",
      encounterId: encounterId,
    });
  }
};

appBus.on("encounter.transcription_created", onTranscriptionCreated);
appBus.on("encounter.snapshot", onEncounterSnapshot);
appBus.on("encounter.completed", onEncounterCompleted);
appBus.on("encounter.finalizing", onEncounterFinalizing);
appBus.on("encounter.reopened", onEncounterReopened);
appBus.on("transcript.snapshot", onTranscriptSnapshot);
appBus.on("transcript.item.created", onTranscriptItemCreated);
appBus.on("encounter.superseded_by_reopen", onEncounterSupersededByReopen);
appBus.on("client.router_params_changed", onParamsChanged);
appBus.on("client.router_search_params_changed", onSearchParamsChanged);

export function __resetForTesting(): void {
  appBus.off("encounter.transcription_created", onTranscriptionCreated);
  appBus.off("encounter.snapshot", onEncounterSnapshot);
  appBus.off("encounter.completed", onEncounterCompleted);
  appBus.off("encounter.finalizing", onEncounterFinalizing);
  appBus.off("encounter.reopened", onEncounterReopened);
  appBus.off("transcript.snapshot", onTranscriptSnapshot);
  appBus.off("transcript.item.created", onTranscriptItemCreated);
  appBus.off("encounter.superseded_by_reopen", onEncounterSupersededByReopen);
  appBus.off("client.router_params_changed", onParamsChanged);
  appBus.off("client.router_search_params_changed", onSearchParamsChanged);

  encounterActor.stop();
}
